import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { NewsletterModal } from "@/components/NewsletterModal";
import { ContactModal } from "@/components/ContactModal";
import { useClickTracking } from "@/hooks/useClickTracking";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useMultilingualContent } from "@/hooks/useMultilingualContent";
import { useAdmin } from "@/hooks/useAdmin";
import { NotificationBell } from "@/components/admin/NotificationBell";

interface SectionConfig {
  section_name: string;
  order_position: number;
  is_visible: boolean;
}

export const Navigation = () => {
  const { t, i18n } = useTranslation();
  const { transformContent } = useMultilingualContent();
  const [isOpen, setIsOpen] = useState(false);
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackClick } = useClickTracking();
  const { isAdmin } = useAdmin();
  
  const [isReady, setIsReady] = useState(false);
  const [showLanguageSwitcher, setShowLanguageSwitcher] = useState(true);

  // Load from sessionStorage immediately if available to prevent flicker
  const cachedContent = sessionStorage.getItem('nav-content');
  const cachedSections = sessionStorage.getItem('nav-sections');

  const [sections, setSections] = useState<SectionConfig[]>(() => {
    if (cachedSections) {
      try {
        return JSON.parse(cachedSections);
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
    return [
      { section_name: 'about', order_position: 1, is_visible: true },
      { section_name: 'tiktok', order_position: 2, is_visible: true },
      { section_name: 'instagram', order_position: 3, is_visible: true },
      { section_name: 'videos', order_position: 4, is_visible: true },
      { section_name: 'blog', order_position: 5, is_visible: true },
      { section_name: 'shop', order_position: 6, is_visible: true },
      { section_name: 'contact', order_position: 7, is_visible: true },
    ];
  });

  const [content, setContent] = useState<Record<string, string>>(() => {
    if (cachedContent) {
      try {
        return JSON.parse(cachedContent);
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
    return {
      nav_logo: "",
      nav_menu_home: "",
      nav_menu_about: "",
      nav_menu_tiktok: "",
      nav_menu_instagram: "",
      nav_menu_videos: "",
      nav_menu_blog: "",
      nav_menu_shop: "",
      nav_cta_button: "",
    };
  });
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the nav element
      if (isOpen && navRef.current && !navRef.current.contains(target)) {
        // Check if the click is inside a dropdown menu (which is rendered in a Portal)
        const isInsideDropdown = (target as Element).closest('[role="menu"]');

        // Only close the menu if the click is not inside a dropdown
        if (!isInsideDropdown) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchContent = async () => {
      // Always fetch fresh data, don't rely on cache
      const { data } = await supabase
        .from("site_content")
        .select("content_key, content_value, content_value_en, content_value_es")
        .in("content_key", ["nav_logo", "nav_menu_home", "nav_menu_about", "nav_menu_tiktok", "nav_menu_instagram", "nav_menu_videos", "nav_menu_blog", "nav_menu_shop", "nav_cta_button", "nav_show_language_switcher", "nav_enabled_languages"]);

      if (data) {
        const transformed = transformContent(data);
        setContent(transformed);
        // Cache the content to prevent flicker on page navigation
        sessionStorage.setItem('nav-content', JSON.stringify(transformed));

        // Load the language switcher visibility setting
        const languageSwitcherSetting = data.find(item => item.content_key === 'nav_show_language_switcher');
        if (languageSwitcherSetting) {
          setShowLanguageSwitcher(languageSwitcherSetting.content_value === 'true');
        }

        // Load enabled languages setting
        const enabledLanguagesSetting = data.find(item => item.content_key === 'nav_enabled_languages');
        if (enabledLanguagesSetting?.content_value) {
          sessionStorage.setItem('nav-enabled-languages', enabledLanguagesSetting.content_value);
        }
        
        // Mark as ready after a small delay to ensure smooth render
        setTimeout(() => setIsReady(true), 50);
      }

      // Fetch section order
      const { data: sectionsData } = await supabase
        .from("section_order")
        .select("section_name, order_position, is_visible")
        .in("section_name", ["about", "tiktok", "instagram", "videos", "blog", "shop", "contact"])
        .order("order_position", { ascending: true });

      if (sectionsData && sectionsData.length > 0) {
        setSections(sectionsData);
        // Cache the sections to prevent flicker on page navigation
        sessionStorage.setItem('nav-sections', JSON.stringify(sectionsData));
      }
    };

    fetchContent();

    // Defer subscriptions to improve LCP
    const subscriptionTimeout = setTimeout(() => {
      // Subscribe to real-time updates for section order changes
      const sectionChannel = supabase.channel(`nav-section-order-changes-${Math.random().toString(36).slice(2)}`, {
        config: {
          broadcast: { self: true }
        }
      });

      sectionChannel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'section_order'
          },
          (payload) => {
            fetchContent();
          }
        )
        .subscribe();

      // Subscribe to real-time updates for site_content changes (nav items, language settings)
      const contentChannel = supabase.channel(`nav-content-changes-${Math.random().toString(36).slice(2)}`, {
        config: {
          broadcast: { self: true }
        }
      });

      contentChannel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'site_content',
            filter: 'content_key=in.(nav_menu_home,nav_menu_about,nav_menu_tiktok,nav_menu_instagram,nav_menu_videos,nav_menu_blog,nav_menu_shop,nav_menu_contact,nav_enabled_languages)'
          },
          (payload) => {
            fetchContent();
          }
        )
        .subscribe();
    }, 1500); // Defer by 1.5 seconds

    return () => {
      clearTimeout(subscriptionTimeout);
    };
  }, [i18n.language]);

  // Helper for language-aware URLs (normalize language code)
  const currentLang = i18n.language?.split('-')[0] || 'pt';
  const languagePrefix = currentLang === 'pt' ? '' : `/${currentLang}`;
  const homePath = languagePrefix || '/';

  // Exclusive video pages belong to the product section, so keep it highlighted
  const isProductPage = /\/(exclusivo|exclusive)\//.test(location.pathname);

  const scrollToSection = (id: string) => {
    // If we're not on the homepage, navigate there first
    const currentPath = location.pathname.replace(/^\/(en|es)/, '') || '/';
    if (currentPath !== '/') {
      navigate(homePath);
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth"
          });
        }
      }, 100);
      setIsOpen(false);
    } else {
      // Already on homepage, just scroll
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth"
        });
        setIsOpen(false);
      }
    }
  };

  const getMenuLabel = (sectionName: string): string => {
    switch (sectionName) {
      case 'about': return content.nav_menu_about;
      case 'tiktok': return content.nav_menu_tiktok;
      case 'instagram': return content.nav_menu_instagram;
      case 'videos': return content.nav_menu_videos;
      case 'blog': return content.nav_menu_blog;
      case 'shop': return content.nav_menu_shop;
      case 'contact': return t('nav.contact');
      default: return '';
    }
  };

  const handleMenuClick = (sectionName: string, label: string, isMobile: boolean = false) => {
    if (sectionName === 'contact') {
      trackClick('navigation', isMobile ? 'Mobile - ' + label : label);
      // Open contact modal directly (works from any page)
      setIsContactOpen(true);
      if (isMobile) setIsOpen(false);
    } else {
      trackClick('navigation', isMobile ? 'Mobile - ' + label : label);
      scrollToSection(sectionName);
    }
  };
  
  return <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 glass-effect shadow-sm transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
      <div className="container max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="font-bold text-2xl gradient-text cursor-pointer hover:scale-105 transition-transform duration-300" onClick={() => {
              trackClick('navigation', 'Logo - Home');
              navigate(homePath);
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }}>{content.nav_logo}</div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            <button onClick={() => {
              trackClick('navigation', content.nav_menu_home);
              scrollToSection('home');
            }} className="text-[15px] xl:text-base font-medium hover:text-accent transition-all duration-300 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full whitespace-nowrap">
              {content.nav_menu_home}
            </button>
            {sections
              .filter(section => section.is_visible && section.section_name !== 'contact')
              .map((section) => {
                const label = getMenuLabel(section.section_name);
                const isActive = section.section_name === 'shop' && isProductPage;
                return (
                  <button
                    key={section.section_name}
                    onClick={() => handleMenuClick(section.section_name, label, false)}
                    className={`text-[15px] xl:text-base font-medium hover:text-accent transition-all duration-300 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full whitespace-nowrap ${
                      isActive ? 'text-accent after:w-full' : 'after:w-0'
                    }`}
                  >
                    {label}
                  </button>
                );
              })
            }
            {isAdmin && (
              <NotificationBell
                onNavigate={(tab, subtab) => {
                  navigate('/admin');
                }}
              />
            )}
            <Button
              variant="gradient"
              size="sm"
              className="shadow-md hover:shadow-glow transition-all duration-300 min-w-[120px]"
              onClick={() => {
                trackClick('cta', 'Navigation CTA - ' + content.nav_cta_button);
                setIsNewsletterOpen(true);
              }}
            >
              {content.nav_cta_button || '\u00A0'}
            </Button>
            {showLanguageSwitcher && <LanguageSwitcher />}
          </div>

          {/* Mobile Menu Button and Notification Bell */}
          <div className="flex items-center gap-3 lg:hidden">
            {isAdmin && (
              <NotificationBell
                onNavigate={(tab, subtab) => {
                  navigate('/admin');
                }}
              />
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && <div className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-4">
              <button onClick={() => {
                trackClick('navigation', 'Mobile - ' + content.nav_menu_home);
                scrollToSection('home');
              }} className="text-left py-2 font-medium hover:text-accent transition-colors">
                {content.nav_menu_home}
              </button>
              {sections
                .filter(section => section.is_visible)
                .map((section) => {
                  const label = getMenuLabel(section.section_name);
                  const isActive = section.section_name === 'shop' && isProductPage;
                  return (
                    <button
                      key={section.section_name}
                      onClick={() => handleMenuClick(section.section_name, label, true)}
                      className={`text-left py-2 font-medium hover:text-accent transition-colors ${
                        isActive ? 'text-accent' : ''
                      }`}
                    >
                      {label}
                    </button>
                  );
                })
              }
              <Button
                variant="gradient"
                size="sm"
                className="w-full"
                onClick={() => {
                  trackClick('cta', 'Mobile Nav CTA - ' + content.nav_cta_button);
                  setIsNewsletterOpen(true);
                  setIsOpen(false);
                }}
              >
                {content.nav_cta_button}
              </Button>
              {showLanguageSwitcher && (
                <div className="pt-2 border-t border-border flex justify-center">
                  <LanguageSwitcher onLanguageChange={() => setIsOpen(false)} />
                </div>
              )}
            </div>
          </div>}
      </div>

      <NewsletterModal open={isNewsletterOpen} onOpenChange={setIsNewsletterOpen} />
      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </nav>;
};